import { useState, useEffect } from "react";
import { Form } from "antd";
import dayjs from "dayjs";
import Button from "@/common/elements/Button";
import CommonDatePicker from "@/common/elements/DatePicker";
import FormItem from "@/common/elements/InputElements/FormItem.jsx";
import Select from "@/common/elements/InputElements/Select";
import TextField from "@/common/elements/InputElements/TextField";
import Table from "@/common/elements/Table";
import { StyledFlex, StyledGridCol, StyledGridRow, StyledSectionHeading, StyledSubTableContainer } from "@/common/styles";
import CustomModal from "@/components/CustomModal";
import { TableHeader } from "@/components/InmateDetails/styles";
import { Checkbox } from "antd";
import { getOptionsFromRefList } from "@/util/common_function";

const OffenseDetails = ({ report = {} }) => {
  const [showOffenseModal, setShowOffenseModal] = useState(false);
  const [selectedOffense, setSelectedOffense] = useState(null);
  const [form] = Form.useForm();

  const sanctionOptions = getOptionsFromRefList("Sanction_Type", false, false, true) || [];
  const inmateViolationOptions = getOptionsFromRefList("Inmate_Violation", false, false, true) || [];

  const offenses = report.officerReportOffenses || [];

  const tableRows = offenses.map((offense, index) => {
    const violationOption = inmateViolationOptions.find(
      (opt) => opt.value === offense.inmateViolationId
    );
    return {
      id: offense.sequence || index + 1,
      violation: violationOption ? violationOption.label : offense.inmateViolationId || "N/A",
      comments: offense.comments || "",
      offenseData: offense,
    };
  });

  const tableData = {
    headers: [
      { key: "id", text: "#" },
      { key: "violation", text: "Violation" },
      { key: "comments", text: "Comments" },
    ],
    rows: tableRows,
  };

  const handleRowClick = (rowData) => {
    setSelectedOffense(rowData.offenseData);
  };

  const handleDetailsClick = () => {
    if (selectedOffense) {
      setShowOffenseModal(true);
    }
  };

  useEffect(() => {
    if (selectedOffense) {
      const sanctions = selectedOffense.officerReportOffenseSanctions || [];
      // Dynamically generate checkbox values based on sanctionOptions.
      const sanctionValues = {};
      sanctionOptions.forEach((option) => {
        // Check if any sanction in the offense has a matching sanctionTypeId.
        sanctionValues[`sanction_${option.value}`] = sanctions.some(
          (s) => s.sanctionTypeId === option.value
        );
      });
      form.setFieldsValue({
        inmateViolation: selectedOffense.inmateViolationId || "",
        comments: selectedOffense.comments || "",
        sanctionEndDate: selectedOffense.sanctionEndDate
          ? dayjs(selectedOffense.sanctionEndDate, "MM/DD/YYYY")
          : null,
        ...sanctionValues,
      });
    }
  }, [selectedOffense, form, sanctionOptions]);

  return (
    <>
      <StyledSubTableContainer>
        <TableHeader justify="space-between">
          <h2>List of offenses this inmate is being charged with</h2>
          <StyledFlex gap={"10px"}>
            <Button onClick={handleDetailsClick} disabled={!selectedOffense}>
              Details
            </Button>
          </StyledFlex>
        </TableHeader>

        <Table
          headers={tableData.headers}
          data={tableData.rows}
          variant="outlined"
          onRowClick={handleRowClick}
        />
      </StyledSubTableContainer>

      {showOffenseModal && (
        <CustomModal
          show={showOffenseModal}
          header="Offense Details"
          size="lg"
          hideFooter={true}
          onHide={() => {
            setShowOffenseModal(false);
            setSelectedOffense(null);
          }}
        >
          <Form form={form} layout="vertical">
            <StyledSectionHeading>
              <span>Offense Information</span>
            </StyledSectionHeading>
            <StyledGridRow gutter={16} justify="start">
              <StyledGridCol xs={24}>
                <FormItem label="Inmate Violation" name="inmateViolation">
                  <Select
                    options={getOptionsFromRefList("Inmate_Violation", false, false, true)}
                    placeholder="Select Offense"
                  />
                </FormItem>
              </StyledGridCol>
              <StyledGridCol xs={24}>
                <FormItem label="Comments" name="comments">
                  <TextField />
                </FormItem>
              </StyledGridCol>
            </StyledGridRow>

            <StyledSectionHeading>
              <span>Sanction Information</span>
            </StyledSectionHeading>
            <StyledGridRow gutter={16} justify="start">
              <StyledGridCol xs={6}>
                <FormItem label="Sanction End Date" name="sanctionEndDate">
                  <CommonDatePicker showTime={false} />
                </FormItem>
              </StyledGridCol>
            </StyledGridRow>
            <StyledGridRow gutter={16}>
              {sanctionOptions.map((option) => (
                <StyledGridCol xs={8} key={option.value}>
                  <FormItem
                    label={option.label}
                    layout="horizontal"
                    labelPostion="label-right"
                    name={`sanction_${option.value}`}
                    valuePropName="checked"
                  >
                    <Checkbox />
                  </FormItem>
                </StyledGridCol>
              ))}
            </StyledGridRow>
          </Form>
        </CustomModal>
      )}
    </>
  );
};

export default OffenseDetails;
